import { ReactNode, useState, useEffect } from 'react';
import { DndBarNode } from '../../dndBar';
import { registerNode, AdditionalNode, AdditionalComponentProps, ValidateClaimFn } from '.././additionalNodes';
import {guidGenerator} from '../../../../utils/utils';
import { JE_JsonValidationContext } from '../../../../jsonParsing/interfaces';
import {FQSchemaData} from '../../../../jsonParsing/fqSpecGeneration';
import useComponentUIHook from '../../componentUI/useComponentUIHook';
import TemplatizedFieldComponent, {getTemplatizedValue, TemplatizedValue} from '../../componentUI/templatizedFieldComponent'; 
import SessionManagement, {sessionFieldsAreValid} from '../../componentUI/sessionManagement';
import { jsonEditorStyles } from '../../jsonEditorStyles';
import { SelectComponent } from '../../../uiComponents';
import { Typography, SelectChangeEvent, Button, FormControl } from '@mui/material';
import { OptionProps } from '../../../../view-models';
import { cloneDeep } from 'lodash';
import {BuildSettings, extractTemplatizableField} from '../../../../jsonParsing/utils';
import { 
  createFQClaimXform, JE_FQClaimXformSpec,
  JE_FQClaimSpec, JE_FQContentDefinitionSpec,
  createFQClaim,
  createFQDisplayControl,
  createFQTechnicalProfile,
  fetchDefaultFQContentDefinitionRef,
  getOrCreateFQContentDefinition
} from '../../../../jsonParsing/jsonFQContext';
import { addFQOrchestrationStepOrValidationProfileRef } from '../../../../jsonParsing/fqSpecGeneration';
import constants from '../../../../constants/jsonXMLConstants';
import { createOrGetVerifyProfiles, createVerifyTOTPSelfAsserted } from './shared';

interface AuthAppRegistrationSchema {
  issuerName: string;
  idClaim: string;
  objectIdClaim: string;
  identifierClaim: string;
  contentDefinitionUrl: string;
};

const configData = {
 contentDefinition: {
   displayName: "Content Definition"
 },
 sessionManagement: true,
 issuerName: "Issuer Name",
 sessionTypes: 'default'
}

function EditNodeUI({data, closeGritComponent, setUnsavedChanges}: AdditionalComponentProps) {
  const classes = jsonEditorStyles();
  const [disableSubmit, setDisableSubmit] = useState(true);
  const {
    modifiedData,
    setModifiedData,
    allClaims,
    checkIfSameIgnoringSubFlows,
    modifiedTemplatizations,
    setModifiedTemplatizations
  } = useComponentUIHook(data);
  
  useEffect(() => {
    try {
      const _res: boolean = checkIfSameIgnoringSubFlows();
      setDisableSubmit(
        _res ||
          (!(
            modifiedData['idClaim']
          )) ||
          (!sessionFieldsAreValid(modifiedData))
      );
      setUnsavedChanges(_res);
    } catch( error ) {
      console.log(error)
    }
  }, [modifiedData]);

  return <FormControl>
    <div className={classes.flexCol}>
      <div className={classes.flexRow}>
        <TemplatizedFieldComponent
          modifiedTemplatizations={modifiedTemplatizations}
          setModifiedTemplatizations={setModifiedTemplatizations}
          data={{
            fieldPath: 'contentDefinition',
            label: configData['contentDefinition']['displayName'],
            style: { margin: 1, width: 600 },
            value: getTemplatizedValue(modifiedData['contentDefinition']),
            variant: 'text'
          }}
          changeHandler={(e: TemplatizedValue) => {
            setModifiedData({ ...modifiedData, contentDefinition: { ...e } });
          }}
        />
      </div>
      <Typography variant="body1" sx={{ pt: 1 }}>
        Select an application name that will appear in the user's authenticator app
      </Typography>
      <div className={classes.flexRow}>
        <TemplatizedFieldComponent
          modifiedTemplatizations={modifiedTemplatizations}
          setModifiedTemplatizations={setModifiedTemplatizations}
          data={{
            fieldPath: 'issuerName',
            label: configData['issuerName'],
            style: { margin: 1, width: 600 },
            value: getTemplatizedValue(modifiedData['issuerName']),
            variant: 'text'
          }}
          changeHandler={(e: TemplatizedValue) => {
            setModifiedData({ ...modifiedData, issuerName: e });
          }}
        />
      </div>
      <Typography variant="body1" sx={{ pt: 1 }}>
        Select a claim to use as a user identity in the Authenticator app:
      </Typography>
      <div className={classes.flexRow} style={{ alignItems: 'center' }}>
        <SelectComponent
          changeHandler={(e: SelectChangeEvent<string>) => {
            setModifiedData({
              ...modifiedData,
              identifierClaim: e.target.value,
            });
          }}
          data={{
            value: modifiedData.identifierClaim ? modifiedData.identifierClaim : '',
            label: 'Authenticator Identifier',
            required: true,
            style: { margin: 1, width: 200 },
            options: Object.keys(allClaims)
              .filter((c) => {
                return allClaims[c]?.dataType === 'string';
              })
              .reduce((acc: OptionProps[], curVal: string) => {
                return acc.concat({ label: curVal, value: curVal });
              }, [])
              .sort((a: OptionProps, b: OptionProps) => {
                return a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1;
              }),
          }}
        />
      </div>
      <Typography variant="body1" sx={{ pt: 1 }}>
        Select claim containing user's objectId for use in credential generation:
      </Typography>
      <div className={classes.flexRow} style={{ alignItems: 'center' }}>
        <SelectComponent
          changeHandler={(e: SelectChangeEvent<string>) => {
            setModifiedData({
              ...modifiedData,
              objectIdClaim: e.target.value,
            });
          }}
          data={{
            value: modifiedData.objectIdClaim ? modifiedData.objectIdClaim : '',
            label: 'objectId Claim',
            required: true,
            style: { margin: 1, width: 200 },
            options: Object.keys(allClaims)
              .filter((c) => {
                return allClaims[c]?.dataType === 'string';
              })
              .reduce((acc: OptionProps[], curVal: string) => {
                return acc.concat({ label: curVal, value: curVal });
              }, [])
              .sort((a: OptionProps, b: OptionProps) => {
                return a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1;
              }),
          }}
        />
      </div>
      <Typography variant="body1" sx={{ pt: 1 }}>
        Select claim containing user principal name for use in credential generation:
      </Typography>
      <div className={classes.flexRow} style={{ alignItems: 'center' }}>
        <SelectComponent
          changeHandler={(e: SelectChangeEvent<string>) => {
            setModifiedData({
              ...modifiedData,
              idClaim: e.target.value,
            });
          }}
          data={{
            value: modifiedData.idClaim ? modifiedData.idClaim : '',
            label: 'principalId Claim',
            required: true,
            style: { margin: 1, width: 200 },
            options: Object.keys(allClaims)
              .filter((c) => {
                return allClaims[c]?.dataType === 'string';
              })
              .reduce((acc: OptionProps[], curVal: string) => {
                return acc.concat({ label: curVal, value: curVal });
              }, [])
              .sort((a: OptionProps, b: OptionProps) => {
                return a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1;
              }),
          }}
        />
      </div>
      {configData.sessionManagement && (
        <SessionManagement
          modifiedData={modifiedData}
          changeHandler={(data: any) => setModifiedData(data)}
          sessionTypes={configData.sessionTypes}
        />
      )}
    <Button
      type="submit"
      fullWidth
      variant="contained"
      disabled={disableSubmit}
      sx={{ mt: 3, mb: 2 }}
      onClick={() => {
        closeGritComponent(cloneDeep(modifiedData), modifiedTemplatizations);
      }}
    >
      Save
    </Button>
  </div> 
  </FormControl>
}

class AuthenticatorAppRegistration implements AdditionalNode {
  componentType = "authenticatorAppRegistration";
  label =  "Authenticator App Registration" 
  
  getDndComponent(): ReactNode {
    return <DndBarNode componentType={this.componentType} label = {this.label}/>
  }

  getJSONCompDef() {
    return {
      nodeType: 'process',
      nodeSubType: 'default',
      label: this.label,
      idClaim: "",
      identifierClaim: "",
      contentDefinition: {
        displayName: 'Content Definition',
      }
    }
  }

  initNodeJSON() {
    return {
      id: `${this.componentType}-` + guidGenerator(),
      componentType: this.componentType,
      contentDefinition: "",
      idClaim: "",
      objectIdClaim: "",
      identifierClaim: "",
      issuerName: "",
      sessionManagement: {
        option: 'alwaysExecute',
        sessionId: ''
      }
    }
  } 
  
  EditNodeUI( props: AdditionalComponentProps) {
    return <EditNodeUI {...props}/>
  }

  validateJSON(
  nodeData: any,
  vCtx: JE_JsonValidationContext,
  addErrorReport: (message: string) => void,
  validateClaim: ValidateClaimFn
  ): void {
    const nodeSettings = nodeData.nodeData;
    if (!nodeSettings['idClaim']) {
      addErrorReport("Registering Authenticator app requires an principalID Claim");
    } else {
      validateClaim(
        nodeSettings['idClaim'],
        "Authenticator User Id Claim",
        true
      )
    }
    if (!nodeSettings['objectIdClaim']) {
      addErrorReport("Registering Authenticator app requires an objectId Claim");
    } else {
      validateClaim(
        nodeSettings['objectIdClaim'],
        "Authenticator User Id Claim",
        true
      )
    }
    if (!nodeSettings['identifierClaim']) {
      addErrorReport("Registering Authenticator app requires an identifier Claim");
    } else {
      validateClaim(
        nodeSettings['identifierClaim'],
        "Authenticator identifier Claim",
        true
      )
    }
  }
  
  getOutputClaims(nodeData: any, validationContext: JE_JsonValidationContext): string[] {
      return [nodeData.idClaim, nodeData.objectIdClaim]
  }
  
  extractFCNData(
      nodeData: any,
      componentType: string,
      buildSettings: BuildSettings,
      linkType?: string
  ): AuthAppRegistrationSchema {
    const issuerName = nodeData.issuerName.templatized? extractTemplatizableField(
      nodeData.issuerName, 
      buildSettings
    ): nodeData.issuerName.value;  
    const idClaim = nodeData.idClaim;
    const objId = nodeData.objectIdClaim;
    const identifierClaim = nodeData.identifierClaim;
    const contentDefinition = extractTemplatizableField( 
      nodeData.contentDefinition, buildSettings
    )
    return {
      //application display name 
      identifierClaim: identifierClaim,
      // upn
      idClaim: idClaim,
      objectIdClaim: objId,
      issuerName: issuerName,  
      contentDefinitionUrl: contentDefinition 
    };
  }

  generateFQSchema({
    node,
    nodes,
    journeyId,
    parsingCtx,
    branchVarMap,
    preconditions
  }: FQSchemaData) {
    //@ts-ignore
    const data: AuthAppRegistrationSchema = node.data;
    const ids = createOrGetVerifyProfiles(parsingCtx, data.objectIdClaim, data.idClaim);
    const MFA_TOTP = createVerifyTOTPSelfAsserted(
      parsingCtx, 
      node.sanitizedDisplayName + '_MFA_STEP',
      ids,
      data.idClaim,
      data.contentDefinitionUrl
    )
    
    const issuerClaim = createFQClaim(parsingCtx.claimStore, {
      id: "issuerName",
      dataType: "string",
      displayName: "Authenticator Issuer"
    });
    
    const totpIdentifierClaim = createFQClaim(parsingCtx.claimStore, {
      id: "totpIdentifier",
      dataType: "string",
      displayName: "User Identifier for TOTP MFA",
      userInputType: "Readonly"
    });

    const uriLabelClaim = createFQClaim(parsingCtx.claimStore, {
      id: "uriLabel",
      dataType: "string",
      displayName: "URI Label",
      userInputType: "Readonly"
    });

    const qrCodeContentClaim = createFQClaim(parsingCtx.claimStore, {
      id: "qrCodeContent",
      dataType: "string",
      displayName: "Register Authenticator app code",
      userInputType: "Readonly"
    });

    const issuerName = data.issuerName;
    const createIssuer: JE_FQClaimXformSpec = createFQClaimXform(
      parsingCtx.claimTransformStore, { 
      id: `createIssuer-${guidGenerator()}`,
      method: "CreateStringClaim",
      inputParameters: [{
        id: "value", dataType: "string", value: issuerName
      }],
      outputClaims: [{
        claimId: issuerClaim.id,
        xformClaimType: "createdClaim"
      }]
    });
    
    const createSecret = createFQClaimXform(parsingCtx.claimTransformStore, {
      id: `generateSecret-${guidGenerator()}`,
      method: "CreateOtpSecret",
      outputClaims: [{
       claimId: ids.secretId,
       xformClaimType: "outputClaim"
      }]
    });
    
    const createURILabel = createFQClaimXform(parsingCtx.claimTransformStore, {
      id: "createUriLabel",
      method: "FormatStringMultipleClaims",
      inputParameters: [{
        id: "stringFormat", dataType: "string", value: "{0}:{1}"
      }],
      inputClaims: [{
        claimId: issuerClaim.id, xformClaimType: "inputClaim1"
      }, {
        claimId: totpIdentifierClaim.id, xformClaimType: "inputClaim2"
      }],
      outputClaims: [{
        claimId: uriLabelClaim.id, xformClaimType: "outputClaim"
      }]
    });
    
    const createIdentifier = createFQClaimXform(parsingCtx.claimTransformStore, {
      id: "createTOTPIdentifier",
      method: "CopyClaim",
      inputClaims: [{ claimId: data.identifierClaim, xformClaimType: "inputClaim"}],
      outputClaims: [{ claimId: totpIdentifierClaim.id, xformClaimType: "outputClaim"}]
    });

    const createUriString = createFQClaimXform(parsingCtx.claimTransformStore, {
      id: "createUriString",
      method: "BuildUri",
      inputClaims: [{
        claimId: uriLabelClaim.id, xformClaimType: "path"
      }, {
        claimId: ids.secretId, xformClaimType: "query.secret"
      }],
      inputParameters: [{
        id: "scheme", dataType: "string", value: "otpauth"
      }, {
        id: "host", dataType: "string", value: "totp"
      }, {
        id: "query.issuer", dataType: "string", value: data.issuerName
      }],
      outputClaims: [{
        claimId: qrCodeContentClaim.id, xformClaimType: "outputClaim"
      }]
    });
    
    const authAppIconControl = createFQDisplayControl(parsingCtx.displayControlStore,  {
      id: "authAppIconControl", userInterfaceControlType: "AuthenticatorAppIconControl",
    });

    const qrCodeDisplay = createFQDisplayControl(parsingCtx.displayControlStore, {
      id: "qrCodeDisplay", userInterfaceControlType: "QrCodeControl",
      inputClaims: [{ claimId: qrCodeContentClaim.id }],
      displayClaims: [{ claimId: qrCodeContentClaim.id, controlClaimType: "QrCodeContent" }]
    });

    const authenticatorInfoDisplay = createFQDisplayControl(parsingCtx.displayControlStore, {
      id:"authenticatorInfoControl",
      userInterfaceControlType: "AuthenticatorInfoControl",
      inputClaims: [{ 
        claimId: totpIdentifierClaim.id 
      }, {
        claimId: ids.secretId 
      }],
      displayClaims: [{
        claimId: totpIdentifierClaim.id
      }, { 
        claimId: ids.secretId
      }]
    });
    const contentDefinitionUrl = data.contentDefinitionUrl;
    let contentDefinition = fetchDefaultFQContentDefinitionRef(
      parsingCtx, "api.selfasserted"
    )
    if(contentDefinitionUrl) {
      const draftCD: JE_FQContentDefinitionSpec = {
        id: `authenticator-mfa-${parsingCtx.contentDefinitionStore.cdIds.size}`,
        loadUri: contentDefinitionUrl,
        recoveryUri: '~/common/default_page_error.html',
        dataUri: 'urn:com:microsoft:aad:b2c:elements:contract:selfasserted:2.1.7',
        metadata: [{ key: 'DisplayName', value: 'Authenticator MFA' }],
      };
      contentDefinition = getOrCreateFQContentDefinition(
        parsingCtx, draftCD, "api.selfasserted.mfa"
      ).id
    }
    const enableTOTPProfile = createFQTechnicalProfile(
      parsingCtx.technicalProfileStore, {
        id: node.sanitizedDisplayName,
        displayName: "EnableTOTPAuth",
        protocol: constants.customPolicySchema.protocols.selfAsserted,
        metadata: [{
          key: "ContentDefinitionReferenceId",
          value: contentDefinition 
        }, { 
          key: "language.button_continue", value: "Continue"
        }],
        cryptographicKeys: [{ id: "issuer_secret", storageReferenceId: "B2C_1A_TokenSigningKeyContainer" }],
        inputClaimXformIds: [
          createIdentifier.id,
          createSecret.id,
          createIssuer.id,
          createURILabel.id,
          createUriString.id
        ],
        inputClaims: [{claimId: qrCodeContentClaim.id}, {claimId:ids.secretId}],
        displayClaims: [{
          displayControlId: authAppIconControl.id
        }, {
          displayControlId: qrCodeDisplay.id
        }, {
          displayControlId: authenticatorInfoDisplay.id
        }],
        outputClaims: [{
          claimId: data.idClaim,
        }, {
          claimId: ids.secretId
        }]
      },
      constants.claimsProviderNames.totp
    );

    addFQOrchestrationStepOrValidationProfileRef(
      parsingCtx,
      node,
      nodes,
      enableTOTPProfile.id,
      "Register-Authenticator-App",
      journeyId,
      branchVarMap,
      preconditions,
    );
    addFQOrchestrationStepOrValidationProfileRef(
      parsingCtx,
      node,
      nodes,
      ids.beginVerifyId,
      "TOTP-Pre-verification-step",
      journeyId,
      branchVarMap,
      preconditions,
    );
    addFQOrchestrationStepOrValidationProfileRef(
      parsingCtx,
      node,
      nodes,
      MFA_TOTP,
      "OTP-Authenticator-App",
      journeyId,
      branchVarMap,
      preconditions,
    );
  }
}

registerNode(new AuthenticatorAppRegistration());
